/**
 * InstagramWebviewEscapeOverlay
 *
 * Full-screen interstitial shown when the user attempts a flow (Stripe checkout)
 * that cannot complete inside the Instagram / Meta in-app webview. Industry-
 * standard pattern: clear instructions + a best-effort intent URL + a copy-link
 * fallback. The container that renders the overlay decides when to open it; this
 * component is purely presentational.
 *
 * PostHog: callers pass `onAction` to log which button the user pressed so the
 * escape funnel becomes observable (`ig_webview_escape_action` is the suggested
 * event name in the caller).
 */

import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

export type IgEscapeAction =
  | "open_intent"
  | "copy_link"
  | "continue_anyway"
  | "close";

export type InstagramWebviewEscapeOverlayProps = {
  open: boolean;
  onClose: () => void;
  /** Optional escape hatch for the rare "I know what I'm doing, try anyway" path. */
  onContinueAnyway?: () => void;
  /** Reported for every interaction so the funnel is observable. */
  onAction?: (action: IgEscapeAction) => void;
  /** Defaults to "es". The user-facing strings live in this file. */
  language?: "es" | "en" | "pt";
  /** URL the user should open in their native browser. Defaults to `window.location.href`. */
  destinationUrl?: string;
  /**
   * When set, replaces the default "open in browser" button (intent / target=_blank).
   * Use for flows that must open a third-party URL (e.g. MercadoPago init_point) in Safari.
   */
  onOpenInBrowser?: () => void;
};

const LABELS: Record<
  "es" | "en" | "pt",
  {
    title: string;
    body: string;
    iosSteps: string;
    androidSteps: string;
    desktopSteps: string;
    openBrowser: string;
    copyLink: string;
    copied: string;
    continueAnyway: string;
    closeAria: string;
    why: string;
  }
> = {
  es: {
    title: "Abrí esto en tu navegador",
    body: "Estás dentro de Instagram. El pago no puede completarse acá. Abrilo en Safari o Chrome para terminar la compra.",
    iosSteps:
      "Tocá los tres puntos (•••) arriba a la derecha y elegí «Abrir en navegador externo».",
    androidSteps:
      "Tocá los tres puntos (⋮) arriba a la derecha y elegí «Abrir en Chrome».",
    desktopSteps:
      "Pegá el link en una nueva pestaña de tu navegador (Safari, Chrome, Firefox).",
    openBrowser: "Abrir en mi navegador",
    copyLink: "Copiar link",
    copied: "Link copiado",
    continueAnyway: "Intentar acá de todos modos",
    closeAria: "Cerrar",
    why: "¿Por qué? El pago usa una conexión segura que Instagram no permite dentro de su app.",
  },
  en: {
    title: "Open this in your browser",
    body: "You are inside Instagram. Checkout cannot complete here. Open this page in Safari or Chrome to finish your purchase.",
    iosSteps:
      "Tap the three dots (•••) at the top right and choose «Open in external browser».",
    androidSteps:
      "Tap the three dots (⋮) at the top right and choose «Open in Chrome».",
    desktopSteps:
      "Paste the link into a new tab of your browser (Safari, Chrome, Firefox).",
    openBrowser: "Open in my browser",
    copyLink: "Copy link",
    copied: "Link copied",
    continueAnyway: "Try here anyway",
    closeAria: "Close",
    why: "Why? Checkout uses a secure connection that Instagram does not allow inside its app.",
  },
  pt: {
    title: "Abra isto no seu navegador",
    body: "Você está dentro do Instagram. O pagamento não pode ser concluído aqui. Abra esta página no Safari ou Chrome para finalizar a compra.",
    iosSteps:
      "Toque nos três pontos (•••) no canto superior direito e escolha «Abrir no navegador externo».",
    androidSteps:
      "Toque nos três pontos (⋮) no canto superior direito e escolha «Abrir no Chrome».",
    desktopSteps:
      "Cole o link em uma nova aba do seu navegador (Safari, Chrome, Firefox).",
    openBrowser: "Abrir no meu navegador",
    copyLink: "Copiar link",
    copied: "Link copiado",
    continueAnyway: "Tentar aqui mesmo assim",
    closeAria: "Fechar",
    why: "Por quê? O pagamento usa uma conexão segura que o Instagram não permite dentro do app.",
  },
};

type Platform = "ios" | "android" | "desktop";

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

/**
 * Build an Android `intent://` URL that opens the destination in Chrome.
 * If Chrome isn't installed, Android still falls back to a chooser, so this is
 * a strict upgrade vs. doing nothing. Returns null for non-Android platforms
 * where this scheme is meaningless.
 */
function buildAndroidChromeIntent(destinationUrl: string): string | null {
  try {
    const u = new URL(destinationUrl);
    // intent://<host><path>?<query>#Intent;scheme=https;package=com.android.chrome;end
    const hostPath = `${u.host}${u.pathname}${u.search}`;
    return `intent://${hostPath}#Intent;scheme=https;package=com.android.chrome;end`;
  } catch {
    return null;
  }
}

export function InstagramWebviewEscapeOverlay({
  open,
  onClose,
  onContinueAnyway,
  onAction,
  language = "es",
  destinationUrl,
  onOpenInBrowser,
}: InstagramWebviewEscapeOverlayProps) {
  const L = LABELS[language] ?? LABELS.es;
  const [copied, setCopied] = useState(false);
  const platform = useMemo<Platform>(() => detectPlatform(), []);

  // Resolve destination URL when the overlay opens; the page may have changed
  // query params (e.g. discount applied) between mount and click. `open` is in
  // the deps on purpose — it's the trigger to re-read `window.location.href`.
  const resolvedDestination = useMemo(() => {
    if (destinationUrl) return destinationUrl;
    if (typeof window === "undefined") return "";
    return window.location.href;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationUrl, open]);

  const androidIntent = useMemo(
    () =>
      platform === "android" ? buildAndroidChromeIntent(resolvedDestination) : null,
    [platform, resolvedDestination],
  );

  // Lock body scroll while open (matches the pattern used elsewhere in the codebase).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset the "Copied" pill if the overlay is reopened.
  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const handleCopy = async () => {
    onAction?.("copy_link");
    try {
      await navigator.clipboard.writeText(resolvedDestination);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // clipboard blocked — fall back to selection-based copy
      const ta = document.createElement("textarea");
      ta.value = resolvedDestination;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2500);
      } catch {
        /* give up silently — the URL is still visible above the button */
      }
      document.body.removeChild(ta);
    }
  };

  const handleOpenIntent = () => {
    onAction?.("open_intent");
    // Android: try intent first (high success). On iOS / desktop: rely on the
    // <a target="_blank"> default — IG iOS opens a new in-app tab which is at
    // least one layer closer to native than the current view.
    if (androidIntent) {
      window.location.href = androidIntent;
    }
  };

  const handleContinueAnyway = () => {
    onAction?.("continue_anyway");
    onContinueAnyway?.();
  };

  const handleClose = () => {
    onAction?.("close");
    onClose();
  };

  const stepsForPlatform =
    platform === "ios" ? L.iosSteps : platform === "android" ? L.androidSteps : L.desktopSteps;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ig-escape-title"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 backdrop-blur-sm px-4 py-6"
      style={{
        // GPU layer — same pattern as the rest of the IG-aware UI in this repo
        WebkitTransform: "translate3d(0,0,0)",
        transform: "translate3d(0,0,0)",
      }}
    >
      <div className="relative w-full max-w-md rounded-2xl bg-zinc-900 text-white shadow-2xl ring-1 ring-white/10 p-6">
        <button
          type="button"
          aria-label={L.closeAria}
          onClick={handleClose}
          className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition"
        >
          <X size={20} aria-hidden="true" />
        </button>

        <h2 id="ig-escape-title" className="text-xl font-semibold pr-8">
          {L.title}
        </h2>
        <p className="mt-3 text-sm text-white/80">{L.body}</p>

        <div className="mt-5 rounded-lg bg-white/5 px-4 py-3 text-sm text-white/90 leading-relaxed">
          {stepsForPlatform}
        </div>

        <div className="mt-5 flex flex-col gap-2">
          {onOpenInBrowser ? (
            <button
              type="button"
              onClick={() => {
                onAction?.("open_intent");
                onOpenInBrowser();
              }}
              className="inline-flex items-center justify-center rounded-xl bg-white text-zinc-900 font-semibold py-3 px-4 min-h-[44px] hover:bg-white/90 active:scale-[.98] transition"
            >
              {L.openBrowser}
            </button>
          ) : platform === "android" && androidIntent ? (
            <button
              type="button"
              onClick={handleOpenIntent}
              className="inline-flex items-center justify-center rounded-xl bg-white text-zinc-900 font-semibold py-3 px-4 min-h-[44px] hover:bg-white/90 active:scale-[.98] transition"
            >
              {L.openBrowser}
            </button>
          ) : (
            // iOS / desktop: target=_blank is the most reliable JS-from-IG escape
            // we have. Even when it stays in IG's popup tab, the user is one tap
            // away from "Open in Safari".
            <a
              href={resolvedDestination}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onAction?.("open_intent")}
              className="inline-flex items-center justify-center rounded-xl bg-white text-zinc-900 font-semibold py-3 px-4 min-h-[44px] hover:bg-white/90 active:scale-[.98] transition"
            >
              {L.openBrowser}
            </a>
          )}

          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center rounded-xl bg-white/10 text-white font-medium py-3 px-4 min-h-[44px] hover:bg-white/15 active:scale-[.98] transition"
          >
            {copied ? L.copied + " ✓" : L.copyLink}
          </button>

          {onContinueAnyway ? (
            <button
              type="button"
              onClick={handleContinueAnyway}
              className="mt-1 text-xs text-white/50 hover:text-white/70 underline underline-offset-2"
            >
              {L.continueAnyway}
            </button>
          ) : null}
        </div>

        <p className="mt-5 text-[11px] leading-relaxed text-white/40">{L.why}</p>
      </div>
    </div>
  );
}

export default InstagramWebviewEscapeOverlay;
